import React, { createContext, useMemo } from 'react';
import { BillingMetrics } from '../../shared/BillingMetrics';

interface BillingMetricsContextData {
  isMetricAllowed: (value: BillingMetrics) => boolean;
  hasEnabledMetric: (value: Array<BillingMetrics>) => boolean;
  allEnabledMetrics: (value: Array<BillingMetrics>) => boolean;
  hasDuplicatedExchangedMessage?: (miuMetricIds: BillingMetrics[], meuMetricIds: BillingMetrics[]) => boolean;
}

interface props {
  tenant: any;
}

export const BillingMetricsContext = createContext<BillingMetricsContextData>({} as BillingMetricsContextData);

export const BillingMetricsContextProvider: React.FC<props> = ({ children, tenant }) => {
  let privateRouteMetrics: any[] = [];
  tenant.privateRoutes.map((privateRoute) => {
    if (privateRoute.option !== 'dataConsumption') {
      privateRouteMetrics = [...privateRouteMetrics, ...privateRoute.metrics];
    }
  });

  const hasEnabledMetric = (metricIds: Array<BillingMetrics>): boolean =>
    privateRouteMetrics.some((privateRouteMetric) => metricIds.some((metricId) => privateRouteMetric === metricId));

  const allEnabledMetrics = (metricIds: Array<BillingMetrics>): boolean =>
    metricIds.every((metricId) => privateRouteMetrics.some((privateRouteMetric) => privateRouteMetric === metricId));

  const isMetricAllowed = (metricId: BillingMetrics): boolean => {
    return privateRouteMetrics.some((allowedMetric) => allowedMetric === metricId);
  };

  const hasDuplicatedExchangedMessage = (
    miuMetricIds: BillingMetrics[],
    meuMetricIds: BillingMetrics[]
  ): boolean => {
    if ((!miuMetricIds && !meuMetricIds) || (miuMetricIds.length > 1 && meuMetricIds.length > 1)) {
      return false;
    }

    const hasMiu = miuMetricIds.length === 1 && miuMetricIds.includes(BillingMetrics.exchangedMessage);
    const hasMeu = meuMetricIds.length === 1 && meuMetricIds.includes(BillingMetrics.exchangedMessage);
  
    return hasMiu && hasMeu;
  };

  const contextValue = useMemo(
    () => ({
      isMetricAllowed,
      hasEnabledMetric,
      allEnabledMetrics,
      hasDuplicatedExchangedMessage,
    }),
    [isMetricAllowed, hasEnabledMetric, allEnabledMetrics, hasDuplicatedExchangedMessage]
  );

  return (
    <BillingMetricsContext.Provider value={contextValue}>
      {children}
    </BillingMetricsContext.Provider>
  );
};

export function useBillingMetricsContext(): BillingMetricsContextData {
  const context = React.useContext(BillingMetricsContext);

  if (!context) {
    throw new Error('use billingmetricscontext context must be used within an BillingMetricsContextProvider');
  }

  return context;
}
